<template>
  <view class="calendar-month">
    <view class="calendar-month-title">{{ title }}</view>
    <view class="calendar-month-days">
      <view
        wx:for="{{days}}"
        wx:key="index"
        wx:style="{{item.type === 'selected' ? activeColor : ''}}"
        wx:class="calendar-month-day {{item.isToday ? 'is-today' : ''}} {{ item.type && 'is-'+ item.type}}"
        bindtap="onClick(index)"
      >
        <text
          wx:if="{{item.supText}}"
          class="calendar-month-topText {{item.holidayType}}"
        >{{item.supText}}</text>
        <text class="calendar-month-text">{{ item.text }}</text>
        <text
          wx:if="{{item.subText && item.subText.text }}"
          class="calendar-month-subText"
          wx:class="{{ {highlight:item.subText.highlight} }}"
        >{{ item.subText.text }}</text>
      </view>
    </view>
  </view>
</template>

<script>
import { createComponent } from '@mpxjs/core'
import {
  getFullDate,
  makeArr,
  getMonthEndDay,
  isToday,
  isLastDay,
  compare,
  formatDate,
  calcDateNum
} from '@didi/ut-utils-lib'

const props = {
  type: {
    type: String,
    value: 'single'
  },
  firstDay: Number,
  minDate: null,
  maxDate: null,
  currentDate: Array,
  color: {
    type: String,
    value: '#FF6400'
  },
  calendarHoliday: Array,
  subTexts: Array,
  startText: String,
  endText: String,
  disableDate: {
    type: Array,
    value: []
  }
}

const watcher = function (option) {
  return Object.keys(props).reduce((watch, key) => {
    watch[key] = function (val, old) {
      this.handlerWatch(val, old)
    }
    return watch
  }, option)
}

createComponent({
  props: {
    ...props,
    date: {
      type: Number,
      optionalTypes: [String, Date],
      value: Date.now()
    }
  },

  data: {
    days: []
  },

  computed: {
    title() {
      const { year, month } = getFullDate(this.date)
      return `${year}年${month}月`
    },
    activeColor() {
      return {
        backgroundColor: this.color
      }
    },
    holidayMap() {
      let map = {}
      this.calendarHoliday?.map((item) => {
        map[item.date] = item
      })
      return map
    },
    subTextsMap() {
      const map = {}
      this.subTexts.forEach(item => {
        map[item.date] = {
          text: item.text,
          highlight: item.highlight || false,
        }
      })
      return map
    }
  },

  watch: watcher({
    date: {
      immediate: true,
      handler(val, old) {
        this.handlerWatch(val, old)
      }
    }
  }),

  methods: {
    handlerWatch(val, old) {
      if (val === old) {
        return
      }
      this.days = this.getDays()
    },

    getDayType(day) {
      const { type, minDate, maxDate, currentDate } = this
      if (compare(day, minDate) < 0 || compare(day, maxDate) > 0 || this.disableDate.some(item=> compare(day, item) === 0)) {
        return 'disabled'
      }

      if (type === 'single') {
        return compare(day, currentDate[0]) === 0 ? 'selected' : ''
      }

      if (type === 'range') {
        return this.getRangeDayType(day)
      }

      return ''
    },
    getRangeDayType(day) {
      const { currentDate } = this
      if (!Array.isArray(currentDate)) {
        return ''
      }

      const [startDay, endDay] = currentDate

      if (!startDay) {
        return ''
      }

      const compareToStart = compare(day, startDay)

      if (!endDay) {
        return compareToStart === 0 ? 'start' : ''
      }

      const compareToEnd = compare(day, endDay)

      if (compareToStart === 0 && compareToEnd === 0) {
        return 'start-end'
      }

      if (compareToStart === 0) {
        return 'start'
      }

      if (compareToEnd === 0) {
        return 'end'
      }

      if (compareToStart > 0 && compareToEnd < 0) {
        return 'middle'
      }

      return ''
    },
    onClick(index) {
      const { type } = this
      const item = this.days[index]
      if (item.type !== 'disabled') {
        this.triggerEvent('click', item)
      }
    },

    getText(type) {
      if (this.type === 'range') {
        if (type === 'start') {
          return this.startText
        }
        if (type === 'end') {
          return this.endText
        }
        if (type === 'start-end') {
          return `${this.startText}/${this.endText}`
        }
      }
      return ''
    },

    getDays() {
      const { date, maxDate, holidayMap, subTextsMap } = this
      const start = new Date(date)
      const { year, month, week } = getFullDate(new Date(start.getFullYear(), start.getMonth(), 1))
      const currentDay = getMonthEndDay(year, month)
      const offset = week < this.firstDay ? 7 - this.firstDay + week : week - this.firstDay
      const total = currentDay + offset

      let holidayText, holidayType

      return makeArr(total).map((item, index) => {
        const day = index - offset + 1
        const fullDate = new Date(year, month - 1, day)
        let type = this.getDayType(fullDate)
        const supText = this.getText(type)
        const today = isToday(fullDate)
        const lastDay = isLastDay(fullDate)

        const subText = subTextsMap ? subTextsMap[formatDate(fullDate)] : null
        
        if (offset > index) {
          return {
            type: 'disabled',
            date: undefined,
            text: undefined
          }
        }


        if (holidayMap) {
          holidayText = holidayMap[formatDate(fullDate)]
            ? holidayMap[formatDate(fullDate)].text
            : null
          holidayType = holidayText && holidayText === '班' && type === '' ? 'text-999' : ''
        }

        if ((holidayText === '班' || holidayText === '休') && type === 'disabled') {
          holidayText = ''
        }

        return {
          type,
          isToday: today,
          date: fullDate.getTime(),
          text: today ? '今' : day,
          subText,
          lastDay,
          supText: supText || holidayText || '',
          holidayType: holidayType ? holidayType : ''
        }
      })
    }
  }
})
</script>

<style lang="stylus" scoped>
.calendar-month
  &-title
    height 38px
    font-size 16px
    font-weight normal
    line-height 38px
    text-align center
    color #333
    letter-spacing 0
    background-color #F3F4F5
    font-family PingFangSC-Semibold
  &-days
    position relative
    display flex
    flex-direction row
    flex-wrap wrap
    user-select none
    padding 6px
  &-day
    position relative
    display flex
    flex-direction column
    width 14.285%
    align-items center
    justify-content center
    height 59px
    text-align center
    color #333
  &-text
    font-family PingFangSC-Medium
    font-weight normal
    font-size 16px
  &-subText
    position absolute
    left 0
    right 0
    bottom 5px
    font-size 12px
    -webkit-transform scale(0.84)
    transform-origin bottom center
    letter-spacing 0
    white-space nowrap
    color #999
    &.highlight
      color #ff5900
  &-topText
    position absolute
    left 0
    right 0
    top 5px
    font-size 12px
    -webkit-transform scale(0.84)
    transform-origin top center
    letter-spacing 0
    white-space nowrap
    color #FF5900
.is-today
  color #FF6400
.is-disabled
  color #cccccc
  cursor default
.is-selected, .is-start, .is-end, .is-start-end, .is-multiple, .is-multiple-middle, .is-multiple-selected
  position relative
  background-color #FF5900
  color #fff
  border-radius 8px
  font-family PingFangSC-Regular
  .calendar-month-subText, .calendar-month-topText
    color #fff
.is-start
  border-top-right-radius 0
  border-bottom-right-radius 0
.is-end
  border-top-left-radius 0
  border-bottom-left-radius 0
.is-multiple-middle
  border-radius 0
.is-middle
  background #FFF5F0
  border-radius 0
  color #333
.popover-calendar-wapper
  position absolute
  bottom 60px
  z-index 999
.blue-text
  color #1473ff
.text-999
  color #999
</style>
<script name='json'>
module.exports = {
  component: true
}
</script>

